import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomSmallerIconDatePicker from "../../common/CustomSmallerIconDatePicker";
import TextField  from "@mui/material/TextField";
import { TABLE_NAME  as tableName } from "../../../helpers";

const isValidDate = (d) => d instanceof Date && !isNaN(d);

const parseDateByFormat = (dateStr, format = "MM/DD/YYYY") => {
  if (!dateStr || typeof dateStr !== "string") return null;

  const trimmedDate = dateStr.trim();
  if (!trimmedDate) return null;

  const parts = trimmedDate.split(/[-/]/);
  if (parts.length === 3) {
    const [part1, part2, part3] = parts;
    const n1 = Number(part1);
    const n2 = Number(part2);
    const n3 = Number(part3);

    if (![n1, n2, n3].some(Number.isNaN)) {
      let day;
      let month;
      let year;

      if (format === "DD-MM-YYYY" || format === "DD/MM/YYYY") {
        day = n1;
        month = n2;
        year = n3;
      } else if (format === "MM/DD/YYYY") {
        month = n1;
        day = n2;
        year = n3;
      }

      if (day && month && year) {
        const parsed = new Date(year, month - 1, day);
        if (
          isValidDate(parsed) &&
          parsed.getFullYear() === year &&
          parsed.getMonth() === month - 1 &&
          parsed.getDate() === day
        ) {
          return parsed;
        }
      }
    }
  }

  const fallback = new Date(trimmedDate);
  return isValidDate(fallback) ? fallback : null;
};

const formatDate = (date, format) => {
  if (!date || !isValidDate(date)) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();

  if (format === "DD-MM-YYYY") return `${dd}-${mm}-${yyyy}`;
  if (format === "MM/DD/YYYY") return `${mm}/${dd}/${yyyy}`;
  return `${dd}/${mm}/${yyyy}`;
};

const createDateColumnFilter = (format = "MM/DD/YYYY") => 
  function DateColumnFilter({ value, header, handleFilterSet }) {

    const getInitialDateValue = () => {
      if (!value || !value.date || !Array.isArray(value.date) || value.date.length === 0) {
        return null;
      }
      const dateStr = value.date[0];
      return parseDateByFormat(dateStr, format);
    };

    const getInitialOperatorValue = () => {
      if (value && typeof value.operator === "string" && value.operator.trim() !== "") {
        return value.operator;
      }
      return "=";
    };

    const [dateValue, setDateValue] = useState(getInitialDateValue);
    const [operatorValue, setOperatorValue] = useState(getInitialOperatorValue);
    const { t } = useTranslation();

    useEffect(() => {
      if (!value || !value.date || !Array.isArray(value.date)) {
        setDateValue(null);
        setOperatorValue("=");
        return;
      }

      if (value.date.length === 0) {
        setDateValue(null);
        setOperatorValue(value?.operator || "=");
        return;
      }

      setDateValue(parseDateByFormat(value.date[0], format));
      setOperatorValue(value?.operator || "=");
    }, [value, format]);

    const handleDateChange = (data) => {
      const nextOperator = data?.operator || operatorValue || "=";
      setOperatorValue(nextOperator);

      if (isValidDate(data.date)) {
        const formatted = formatDate(data.date, format);
        setDateValue(data.date);
        handleFilterSet({ date: [formatted], operator: nextOperator });
      } else {
        setDateValue(null);
        handleFilterSet({ date: [], operator: nextOperator });
      }
    };

    return (
      <CustomSmallerIconDatePicker
        value={dateValue}
        onChange={handleDateChange}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            label={`Edit ${tableName[header]}`}
            placeholder={format}
            inputProps={{
              ...params.inputProps,
              value: formatDate(dateValue, format),
            }}
          />
        )}
        operatorLabel={t('comparison_operator')}
        showOperators={true}
        defaultOperator={operatorValue}
        format={format}
        inputFormat={format}
        mask="__/__/____"
        smallerIcon
      />
    );
  };

const createDateColumnEditor = (format = "MM/DD/YYYY") =>
  function DateColumnEditor({ value, onBlur, header }) {
    const [dateValue, setDateValue] = useState(parseDateByFormat(value, format));
    const [hasChanged, setHasChanged] = useState(false);

    useEffect(() => {
      if (hasChanged) return;
      
      setDateValue(parseDateByFormat(value, format));
    }, [value, hasChanged, format]);

    const handleDateChange = (data) => {
      setHasChanged(true);
      setDateValue(data.date);
      
      const formatted = isValidDate(data.date)
        ? formatDate(data.date, format)
        : undefined;
      
      onBlur(formatted);
    };

    //     const handleDateChange = (date) => {
//       const formatted = isValidDate(date)
//         ? formatDate(date, format)
//         : undefined;
//       setDateValue(date);
//       onBlur(formatted);
//       //handleFilterSet(formatted);
//     };

    return (
      <CustomSmallerIconDatePicker
        value={dateValue}
        onChange={handleDateChange}
        onClose={() => {
          const formatted = dateValue ? formatDate(dateValue, format) : undefined;
          onBlur(formatted);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            autoFocus
            label={`Edit ${header}`}
            placeholder={format}
            inputProps={{
              ...params.inputProps,
              value: formatDate(dateValue, format),
            }}
          />
        )}
        format={format}
        inputFormat={format}
        mask="__/__/____"
        smallerIcon
      />
    );
  };

export { createDateColumnFilter, createDateColumnEditor };